import { Fragment, type ReactNode } from 'react';

const SAFE_HREF = /^(https?:\/\/|mailto:)/i;
// trailing punctuation usually ends the sentence, not the URL
const TRAILING = /[.,;:!?)\]'"]+$/;
const URL_PATTERN = /https?:\/\/[^\s<>]+/g;

function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline underline-offset-2"
    >
      {children}
    </a>
  );
}

/** Plain text with http(s) URLs turned into links that open in a new tab. */
function linkify(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(URL_PATTERN)) {
    const start = match.index;
    const url = match[0].replace(TRAILING, '');
    if (start > last) nodes.push(text.slice(last, start));
    nodes.push(
      <ExternalLink key={`${keyPrefix}${start}`} href={url}>
        {url}
      </ExternalLink>,
    );
    last = start + url.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** Shopper text: plain, line breaks kept, URLs linked. Nothing in it is ever markup. */
export function PlainText({ text }: { text: string }) {
  return (
    <>
      {text.split('\n').map((line, index) => (
        <Fragment key={index}>
          {index > 0 ? <br /> : null}
          {linkify(line, `l${index}-`)}
        </Fragment>
      ))}
    </>
  );
}

// **bold**, *italic* or _italic_, [label](url), bare URLs; first match wins at each position.
const INLINE =
  /\*\*([^*]+?)\*\*|\*([^*\s][^*]*?)\*|_([^_\s][^_]*?)_|\[([^\]]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s<>]+)/g;

function inline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(INLINE)) {
    const start = match.index;
    const key = `${keyPrefix}${start}`;
    const [whole, bold, italicStar, italicUnderscore, label, href, bare] = match;
    let end = start + whole.length;
    if (start > last) nodes.push(text.slice(last, start));
    if (bold !== undefined) {
      nodes.push(<strong key={key}>{inline(bold, `${key}b`)}</strong>);
    } else if (italicStar !== undefined || italicUnderscore !== undefined) {
      nodes.push(<em key={key}>{inline(italicStar ?? italicUnderscore ?? '', `${key}i`)}</em>);
    } else if (label !== undefined && href !== undefined) {
      nodes.push(
        SAFE_HREF.test(href) ? (
          <ExternalLink key={key} href={href}>
            {inline(label, `${key}a`)}
          </ExternalLink>
        ) : (
          label
        ),
      );
    } else if (bare !== undefined) {
      const url = bare.replace(TRAILING, '');
      end = start + url.length;
      nodes.push(
        <ExternalLink key={key} href={url}>
          {url}
        </ExternalLink>,
      );
    }
    last = end;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

type Block =
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] };

const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;

function blocks(text: string): Block[] {
  const out: Block[] = [];
  for (const line of text.replace(/\r\n?/g, '\n').split('\n')) {
    const current = out.at(-1);
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    if (bullet) {
      if (current?.kind === 'ul') current.items.push(bullet[1] ?? '');
      else out.push({ kind: 'ul', items: [bullet[1] ?? ''] });
    } else if (numbered) {
      if (current?.kind === 'ol') current.items.push(numbered[1] ?? '');
      else out.push({ kind: 'ol', items: [numbered[1] ?? ''] });
    } else if (line.trim() === '') {
      out.push({ kind: 'paragraph', lines: [] });
    } else if (current?.kind === 'paragraph') {
      current.lines.push(line);
    } else {
      out.push({ kind: 'paragraph', lines: [line] });
    }
  }
  return out.filter((block) => block.kind !== 'paragraph' || block.lines.length > 0);
}

/**
 * Staff text in the allowed markdown subset (bold, italic, lists, links), built as React nodes:
 * anything else, HTML included, stays literal text. Same subset the backend accepts (D-103).
 */
export function BasicMarkdown({ text }: { text: string }) {
  return (
    <div className="space-y-2">
      {blocks(text).map((block, index) => {
        const key = `b${index}`;
        if (block.kind === 'ul' || block.kind === 'ol') {
          const List = block.kind;
          return (
            <List
              key={key}
              className={block.kind === 'ul' ? 'list-disc pl-5' : 'list-decimal pl-5'}
            >
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{inline(item, `${key}-${itemIndex}-`)}</li>
              ))}
            </List>
          );
        }
        return (
          <p key={key}>
            {block.lines.map((line, lineIndex) => (
              <Fragment key={lineIndex}>
                {lineIndex > 0 ? <br /> : null}
                {inline(line, `${key}-${lineIndex}-`)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
